import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import { siteRoute } from '@/lib/domains';
import { AboutOverlay } from '@/components/AboutOverlay';
import { Footer } from '@/components/shared/Footer';
import { SmartLink } from '@/components/shared/SmartLink';
import { track } from '@/lib/analytics';
import '@/styles/nymphe-v3.css';

const ease = [0.22, 1, 0.36, 1];

const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ delay, duration: 0.95, ease }}
  >
    {children}
  </motion.div>
);

/* ══════════════════════════════════════════════════════════════
   HOMEPAGE — conversion-first editorial landing
   ══════════════════════════════════════════════════════════════ */
const HomePageV3 = () => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const location = useLocation();

  // Nav / footer "About" triggers the overlay from anywhere on this page.
  useEffect(() => {
    window.openAboutOverlay = () => {
      setAboutOpen(true);
      track('story_overlay_opened', { source: 'nav' });
    };
    return () => { delete window.openAboutOverlay; };
  }, []);

  // Honour ?about=1 (Footer "About" from non-home routes deep-links here)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    if (params.get('about') === '1') {
      setAboutOpen(true);
      track('story_overlay_opened', { source: 'nav' });
      params.delete('about');
      const qs = params.toString();
      const clean = location.pathname + (qs ? `?${qs}` : '') + location.hash;
      window.history.replaceState({}, '', clean);
    }
  }, [location.search, location.pathname, location.hash]);

  // Mid-scroll auto-fire removed — the overlay was interrupting the natural
  // editorial flow of the homepage. It remains available as a manual trigger
  // from the top nav / footer "About" link (see window.openAboutOverlay above).

  return (
    <MotionConfig reducedMotion="user">
    <div className="nv3-page nv3-snap" data-testid="home-v3">
      <div className="noise-overlay" />

      {/* About overlay — scroll-triggered, anchored to homepage */}
      <AboutOverlay isOpen={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* ── 1. HERO ── */}
      <section className="nv3-section nv3-hero" data-testid="hero">
        <div className="nv3-hero-video" data-testid="hero-video-container">
          <video
            autoPlay muted loop playsInline preload="auto"
            className="nv3-hero-video-el"
            data-testid="hero-video"
          >
            <source src="/hero-spokes.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="nv3-hero-overlay" />
        <div className="nv3-hero-content">
          <motion.h1
            className="nv3-hero-headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 1.1, ease }}
          >
            Private connection, beautifully curated.
          </motion.h1>
          <motion.p
            className="nv3-hero-sub"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9 }}
          >
            NYMPHÉ is an adults-only discovery platform for trans women, femmes, and respectful admirers who value discretion, consent, and genuine connection.
          </motion.p>
          <motion.div
            className="nv3-hero-ctas"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <SmartLink
              to={siteRoute('/access')}
              className="nv3-btn-primary"
              data-testid="hero-request-access"
              onClick={() => track('hero_primary_cta_click')}
            >
              Request early access
            </SmartLink>
            <SmartLink
              to="/the-app"
              className="nv3-hero-secondary"
              data-testid="hero-see-how"
              onClick={() => track('hero_secondary_cta_click')}
            >
              See how it works
            </SmartLink>
            <p className="nv3-hero-micro" data-testid="hero-trust-strip">
              18+ only · Private invitations · Curated discovery · Privacy-first · Respect required
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 2. WHO IT'S FOR · Audience cards (2-column duo) ── */}
      <section className="nv3-section nv3-duo" data-testid="audience-section">
        <div className="nv3-duo-inner">
          <FadeIn className="nv3-duo-col" data-testid="duo-femmes">
            <p className="nv3-ui-label">FOR TRANS WOMEN &amp; FEMMES</p>
            <h2 className="nv3-duo-head">Be seen without being exposed.</h2>
            <p className="nv3-duo-body">
              Control your visibility, your boundaries, and who gets access to your attention.
            </p>
          </FadeIn>
          <div className="nv3-duo-divider" aria-hidden="true" />
          <FadeIn delay={0.1} className="nv3-duo-col" data-testid="duo-admirers">
            <p className="nv3-ui-label">FOR RESPECTFUL ADMIRERS</p>
            <h2 className="nv3-duo-head">Come correct.</h2>
            <p className="nv3-duo-body">
              NYMPHÉ is for adults who understand consent, discretion, and admiration — not entitlement.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── 3. WHY IT'S DIFFERENT (4 cards) ── */}
      <section className="nv3-section nv3-proof nv3-proof--why" data-testid="why-section">
        {/* Background: static webp (video kept in DOM but hidden via CSS per audit spec) */}
        <div
          className="nv3-why-bg"
          aria-hidden="true"
          style={{ backgroundImage: "url('/assets/why-calm-section.webp')" }}
        >
          <video
            className="nv3-why-bg-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/images/abstract-close.jpg"
            data-testid="why-bg-video"
          >
            <source src="/video/why-bg.mp4" type="video/mp4" />
          </video>
          <div className="nv3-why-bg-tint" />
          <div className="nv3-why-bg-vignette" />
        </div>
        <div className="nv3-proof-inner">
          <FadeIn>
            <p className="nv3-ui-label">WHY IT&apos;S DIFFERENT</p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="nv3-mission-line nv3-mission-line--lead nv3-why-heading">
              A calmer way to find connection.
            </h2>
          </FadeIn>
          <div className="nv3-proof-grid nv3-proof-grid--four">
            <FadeIn delay={0.15}>
              <article className="nv3-proof-card" data-testid="why-curated">
                <span className="nv3-proof-num">I</span>
                <p className="nv3-proof-card-head">Curated discovery, not endless swiping.</p>
                <p className="nv3-proof-card-micro">
                  Fewer random profiles. More intentional connections shaped by identity, preference, location, and connection style.
                </p>
              </article>
            </FadeIn>
            <FadeIn delay={0.22}>
              <article className="nv3-proof-card" data-testid="why-privacy">
                <span className="nv3-proof-num">II</span>
                <p className="nv3-proof-card-head">Privacy-first by design.</p>
                <p className="nv3-proof-card-micro">
                  Choose what you show, what stays hidden, and who gets access to you.
                </p>
              </article>
            </FadeIn>
            <FadeIn delay={0.29}>
              <article className="nv3-proof-card" data-testid="why-boundaries">
                <span className="nv3-proof-num">III</span>
                <p className="nv3-proof-card-head">Boundaries built in.</p>
                <p className="nv3-proof-card-micro">
                  Set preferences and limits before discovery begins.
                </p>
              </article>
            </FadeIn>
            <FadeIn delay={0.36}>
              <article className="nv3-proof-card" data-testid="why-respect">
                <span className="nv3-proof-num">IV</span>
                <p className="nv3-proof-card-head">Respect is the standard.</p>
                <p className="nv3-proof-card-micro">
                  No harassment. No pressure. No impersonation. No fetishising behaviour.
                </p>
              </article>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── 3.5 ROOFTOP INTENTION BEAT — single editorial image moment ── */}
      <section
        className="nv3-intent-beat"
        data-testid="intent-beat-section"
        aria-label="Not everything needs to be loud"
      >
        <picture className="nv3-intent-beat-picture" aria-hidden="true">
          <source media="(max-width: 768px)" srcSet="/assets/home-rooftop-intent-m.jpg" />
          <img
            src="/assets/home-rooftop-intent.jpg"
            alt=""
            loading="lazy"
            decoding="async"
            className="nv3-intent-beat-img"
            data-testid="intent-beat-image"
          />
        </picture>
        <div className="nv3-intent-beat-tint" aria-hidden="true" />
        <div className="nv3-intent-beat-fade" aria-hidden="true" />
        <div className="nv3-intent-beat-inner">
          <FadeIn>
            <p className="nv3-intent-beat-line nv3-intent-beat-line--open">
              Not everything needs to be loud.
            </p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <p className="nv3-intent-beat-line nv3-intent-beat-line--open">
              Not everything should be visible.
            </p>
          </FadeIn>
          <FadeIn delay={0.26}>
            <p className="nv3-intent-beat-line">
              Some spaces aren&rsquo;t made to be explored freely.
            </p>
          </FadeIn>
          <FadeIn delay={0.36}>
            <p className="nv3-intent-beat-line">
              They&rsquo;re entered &mdash; carefully, intentionally.
            </p>
          </FadeIn>
          <FadeIn delay={0.52}>
            <p className="nv3-intent-beat-line nv3-intent-beat-line--key">
              NYMPHÉ isn&rsquo;t built for attention.
            </p>
          </FadeIn>
          <FadeIn delay={0.62}>
            <p className="nv3-intent-beat-line nv3-intent-beat-line--key">
              It&rsquo;s built for intention.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS (5 steps) ── */}
      <section className="nv3-section nv3-proof nv3-proof--how" data-testid="how-section">
        <div className="nv3-proof-inner">
          <FadeIn>
            <p className="nv3-ui-label">HOW IT WORKS</p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="nv3-mission-line nv3-mission-line--lead nv3-how-heading">
              How NYMPHÉ works
            </h2>
          </FadeIn>
          <div className="nv3-proof-steps nv3-proof-steps--five">
            <FadeIn delay={0.12}>
              <div className="nv3-proof-step" data-testid="how-step-1">
                <span className="nv3-proof-numeral">I</span>
                <p className="nv3-proof-beat">Request early access</p>
                <p className="nv3-proof-caption">You&apos;ll be redirected to a private access page to join.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="nv3-proof-step" data-testid="how-step-2">
                <span className="nv3-proof-numeral">II</span>
                <p className="nv3-proof-beat">Create your profile</p>
                <p className="nv3-proof-caption">Choose what you share and what stays discreet.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.28}>
              <div className="nv3-proof-step" data-testid="how-step-3">
                <span className="nv3-proof-numeral">III</span>
                <p className="nv3-proof-beat">Set your preferences</p>
                <p className="nv3-proof-caption">Filter by identity, intention, and location.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.36}>
              <div className="nv3-proof-step" data-testid="how-step-4">
                <span className="nv3-proof-numeral">IV</span>
                <p className="nv3-proof-beat">Discover with intention</p>
                <p className="nv3-proof-caption">A calmer, more considered experience — not endless swiping.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.44}>
              <div className="nv3-proof-step" data-testid="how-step-5">
                <span className="nv3-proof-numeral">V</span>
                <p className="nv3-proof-beat">Connect safely</p>
                <p className="nv3-proof-caption">Block, report, or step away at any time.</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── 4.5 INTENTION BRIDGE — editorial page-break image ── */}
      <div
        className="nv3-intention-bridge"
        data-testid="infinity-bridge"
      >
        <FadeIn className="nv3-intention-bridge-frame">
          <picture className="nv3-intention-bridge-picture">
            <source media="(max-width: 768px)" srcSet="/assets/nymphe-intention-bridge-m.jpg" />
            <img
              src="/assets/nymphe-intention-bridge.jpg"
              alt=""
              loading="lazy"
              decoding="async"
              className="nv3-intention-bridge-img"
              data-testid="intention-bridge-image"
              aria-hidden="true"
            />
          </picture>
          <div className="nv3-intention-bridge-tone" aria-hidden="true" />
          <div className="nv3-intention-bridge-vignette" aria-hidden="true" />
        </FadeIn>
      </div>

      {/* ── 5. INSIDE THE EXPERIENCE — integrated product-story stanzas ── */}
      <section className="nv3-section nv3-ui-pair" data-testid="ui-pair-section">
        <div className="nv3-ui-pair-inner">
          <FadeIn>
            <p className="nv3-ui-label">INSIDE THE EXPERIENCE</p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="nv3-ui-pair-head">Inside the experience</h2>
          </FadeIn>
          <FadeIn delay={0.14}>
            <p className="nv3-ui-pair-sub" data-testid="ui-pair-sub">
              Discovery, conversation, and privacy controls — designed into the way NYMPHÉ works.
            </p>
          </FadeIn>

          <div className="nv3-ui-stanzas">
            {/* Stanza I · Discovery — image left, copy right */}
            <FadeIn delay={0.16} className="nv3-ui-stanza nv3-ui-stanza--image-left" data-testid="ui-stanza-discovery">
              <div className="nv3-ui-stanza-frame nv3-ui-pair-frame">
                <img
                  src="/images/home-discover-ui-polished.png"
                  alt="NYMPHÉ Discover screen on iPhone — a 4-profile Nearby grid with names, ages, distances, verified badges, and the Discover bottom navigation."
                  loading="lazy"
                  decoding="async"
                  className="nv3-ui-pair-img"
                  data-testid="home-ui-discovery"
                />
              </div>
              <div className="nv3-ui-stanza-copy">
                <p className="nv3-ui-stanza-num">I</p>
                <p className="nv3-ui-stanza-eyebrow">Discovery</p>
                <h3 className="nv3-ui-stanza-head">Curated, not endless.</h3>
                <p className="nv3-ui-stanza-body">
                  Verified profiles shaped by identity, preference, and proximity — a considered view of people who match how you want to connect.
                </p>
                <p className="nv3-ui-stanza-quiet">
                  <SmartLink
                    to="/the-app"
                    className="nv3-ui-stanza-quiet-link"
                    data-testid="ui-stanza-discovery-link"
                  >
                    See how the app works
                  </SmartLink>
                </p>
              </div>
            </FadeIn>

            {/* Stanza II · Conversations — copy left, image right */}
            <FadeIn delay={0.22} className="nv3-ui-stanza nv3-ui-stanza--image-right" data-testid="ui-stanza-conversations">
              <div className="nv3-ui-stanza-copy">
                <p className="nv3-ui-stanza-num">II</p>
                <p className="nv3-ui-stanza-eyebrow">Conversations</p>
                <h3 className="nv3-ui-stanza-head">Mutual from the start.</h3>
                <p className="nv3-ui-stanza-body">
                  Private threads open only when both people choose to. No unwanted messages. No pressure. No noise.
                </p>
              </div>
              <div className="nv3-ui-stanza-frame nv3-ui-pair-frame">
                <img
                  src="/images/home-messages-ui-polished.png"
                  alt="NYMPHÉ Messages screen on iPhone — Conversations tab with verified threads, last-active timestamps, location-shared and voice-message indicators."
                  loading="lazy"
                  decoding="async"
                  className="nv3-ui-pair-img"
                  data-testid="home-ui-messages"
                />
              </div>
            </FadeIn>

            {/* Stanza III · Privacy Controls — image left, copy right */}
            <FadeIn delay={0.28} className="nv3-ui-stanza nv3-ui-stanza--image-left" data-testid="ui-stanza-privacy">
              <div className="nv3-ui-stanza-frame nv3-ui-pair-frame">
                <img
                  src="/images/home-privacy-controls-ui-polished.png"
                  alt="NYMPHÉ Privacy Controls screen on iPhone — Incognito Mode, Anonymous Viewing, Hide Online Status, and Read Receipts toggles, with a Noir tier badge."
                  loading="lazy"
                  decoding="async"
                  className="nv3-ui-pair-img"
                  data-testid="home-ui-privacy"
                />
              </div>
              <div className="nv3-ui-stanza-copy">
                <p className="nv3-ui-stanza-num">III</p>
                <p className="nv3-ui-stanza-eyebrow">Privacy Controls</p>
                <h3 className="nv3-ui-stanza-head">Every lever is yours.</h3>
                <p className="nv3-ui-stanza-body">
                  Incognito mode, anonymous viewing, hidden online status, and read-receipt toggles — all tunable in one place.
                </p>
                <p className="nv3-ui-stanza-quiet">
                  <SmartLink
                    to="/safety"
                    className="nv3-ui-stanza-quiet-link"
                    data-testid="ui-stanza-privacy-link"
                  >
                    Explore safety &amp; privacy
                  </SmartLink>
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── 6. SAFETY & PRIVACY ── */}
      <section className="nv3-section nv3-trust" data-testid="safety-section">
        <div className="nv3-trust-inner">
          <FadeIn>
            <p className="nv3-ui-label">SAFETY &amp; PRIVACY</p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="nv3-trust-line nv3-trust-line--head" data-testid="safety-heading">
              Private does not mean unprotected.
            </h2>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="nv3-trust-line nv3-trust-line--body" data-testid="safety-body">
              NYMPHÉ is built with safety, control, and discretion at its core.
            </p>
          </FadeIn>
          <FadeIn delay={0.24}>
            <p className="nv3-trust-line" data-testid="safety-1">18+ only platform</p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="nv3-trust-line" data-testid="safety-2">Profile visibility controls</p>
          </FadeIn>
          <FadeIn delay={0.36}>
            <p className="nv3-trust-line" data-testid="safety-3">Block &amp; report tools</p>
          </FadeIn>
          <FadeIn delay={0.42}>
            <p className="nv3-trust-line" data-testid="safety-4">Preference &amp; boundary filters</p>
          </FadeIn>
          <FadeIn delay={0.48}>
            <p className="nv3-trust-line" data-testid="safety-5">Clear community standards</p>
          </FadeIn>
          <FadeIn delay={0.54}>
            <p className="nv3-trust-line" data-testid="safety-6">Zero tolerance for harassment, scams, or impersonation</p>
          </FadeIn>
          <FadeIn delay={0.62}>
            <p className="nv3-trust-quiet">
              <SmartLink
                to="/safety"
                className="nv3-trust-quiet-link"
                data-testid="safety-quiet-link"
              >
                Explore safety &amp; privacy
              </SmartLink>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── 6.5 CLOSING VISUAL ANCHOR — leads into the final CTA ── */}
      <section
        className="nv3-closing-anchor"
        data-testid="closing-anchor-section"
        aria-label="Not everyone belongs here"
      >
        <picture className="nv3-closing-anchor-picture" aria-hidden="true">
          <source media="(max-width: 768px)" srcSet="/assets/home-closing-beat.png" />
          <img
            src="/assets/home-closing-beat.png"
            alt=""
            loading="lazy"
            decoding="async"
            className="nv3-closing-anchor-img"
            data-testid="closing-anchor-image"
          />
        </picture>
        <div className="nv3-closing-anchor-tint" aria-hidden="true" />
        <div className="nv3-closing-anchor-fade" aria-hidden="true" />
        <div className="nv3-closing-anchor-inner">
          <FadeIn>
            <h2 className="nv3-closing-anchor-head" data-testid="closing-anchor-head">
              Not everyone belongs here.
            </h2>
          </FadeIn>
          <FadeIn delay={0.18}>
            <p className="nv3-closing-anchor-sub" data-testid="closing-anchor-sub">
              For those who do, it feels effortless.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── 7. EARLY ACCESS CTA (no form) ── */}
      <section className="nv3-section nv3-close" data-testid="final-cta">
        <div className="nv3-close-glow" />
        <div className="nv3-close-inner">
          <FadeIn>
            <h2 className="nv3-close-headline">Early access is limited.</h2>
          </FadeIn>
          <FadeIn delay={0.12}>
            <p className="nv3-close-sub">
              Private invitations are released in waves to protect the quality and safety of the space.
            </p>
          </FadeIn>
          <FadeIn delay={0.22}>
            <SmartLink
              to={siteRoute('/access')}
              className="nv3-btn-cta"
              data-testid="final-request-access"
              onClick={() => track('final_cta_click')}
            >
              Request early access
            </SmartLink>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="nv3-close-expect" data-testid="final-expectation">
              You&apos;ll be taken to a private access page to enter your details.
            </p>
          </FadeIn>
          <FadeIn delay={0.42}>
            <div className="nv3-close-positioning" data-testid="positioning-line">
              <p className="nv3-close-positioning-head">Connection, without the noise.</p>
              <p className="nv3-close-positioning-sub">Private. Curated. Intentional.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer inside scroll container */}
      <Footer />
    </div>
    </MotionConfig>
  );
};

export default HomePageV3;
